import FormBlock from '../form-block';
import ShiftBindingItem from './shift-binding-item';
import React, {useEffect, useState} from 'react';
import ShiftBindingForm from './shift-binding-form';
import {useStaffs} from '../../../../api/staff';
import {useUnits} from '../../../../api/unit';
import {EmployeeSchedule, Shift} from '../../../../api/types';
import {useForm, FormProvider} from 'react-hook-form';
import {flatMap} from 'lodash';
import * as uuid from 'uuid';
import {DeleteDialog} from '../../../../components/Dialog';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import {faSpinner} from '@fortawesome/pro-light-svg-icons';
import ShiftInfo from '../shift-info';

type ShiftBindingBlockProps = {
  onChange: (employeeSchedules: EmployeeSchedule[]) => void;
  onInit: (employeeSchedules: EmployeeSchedule[]) => void;
  shifts: Shift[];
  employeeSchedules: EmployeeSchedule[];
};
function ShiftBindingBlock({
  onChange,
  onInit,
  shifts,
  employeeSchedules,
}: ShiftBindingBlockProps) {
  const [showShiftBindingForm, setShowShiftBindingForm] = useState(false);
  const [editData, setEditData] = useState<EmployeeSchedule | null>(null);
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const {data: staffs, isLoading: isStaffLoading} = useStaffs();
  const {data: units, isLoading: isUnitLoading} = useUnits();
  const methods = useForm<EmployeeSchedule>();
  const {reset, watch} = methods;
  const isCurrentSelectCompany = employeeSchedules.some(s => s.appliedUnit === 'Company' && s.id === editData?.id);
  const isCompanyUnselected = employeeSchedules.every(s => s.appliedUnit !== 'Company');
  const id = watch('id');

  useEffect(() => {
    if (staffs && units) {
      const newData = employeeSchedules
        .map(e => {
          return {
            ...e,
            units: e.units.filter(
              u =>
                staffs?.find(s => s.id === u.id) ||
                units?.find(d => d.id === u.id),
            ),
          };
        })
        .filter(e => e.units.length > 0 || e.appliedUnit === 'Company');

      onInit(newData);
    }
  }, [staffs, units]);

  const selectedUnits = flatMap(
    employeeSchedules.filter(e => e.appliedUnit === 'Organization'),
    e => e.units.map(u => ({...u, formId: e.id})),
  );
  const selectedStaffs = flatMap(
    employeeSchedules.filter(e => e.appliedUnit === 'Employee'),
    e => e.units.map(u => ({...u, formId: e.id})),
  );

  const isLoading = isStaffLoading || isUnitLoading;
  return (
    <FormBlock
      title={
      <span>
        步驟3. 排班員工設定
        <ShiftInfo />
      </span>}
      formType={''}
      onAddItem={() => {
        reset({});
        setEditData(null)
        setShowShiftBindingForm(true);
      }}
      disabled={shifts?.length === 0 || isStaffLoading || isUnitLoading}
      showBigAddButton={employeeSchedules?.length === 0}
      data-test-id={'shift-binding'}
    >
      {isLoading && (
        <div className={'flex items-center justify-center gap-[15px] mb-5'}>
          資料載入中
          <FontAwesomeIcon icon={faSpinner} pulse size={'1x'} />
        </div>
      )}
      {!isLoading && (
        <>
          <p className={'py-[10px] px-5 bg-[#eff0f0] m-[10px]'}>
            您可將班別套用在公司、部門、人員身上，這樣進入 Apollo XE 就可以直接開始排班囉！
            <br/>
            <br/>
            範例：公司大多為「班別A」，僅行政部為「班別B」，僅行政部的王大明為「班別C」。您可以將「公司套用班別A」、「行政部套用班別 B」、「王大明套用班別 C」即可達成您所需的設定。
          </p>
          {employeeSchedules?.map((e, idx) => (
            <ShiftBindingItem
              key={`shift_binding_item_${idx}`}
              item={{
                ...e,
                shift: {
                  ...e.shift,
                  shiftName:
                    shifts.find(s => s.id === e.shift.shiftId)?.storeName || '',
                },
              }}
              idx={idx}
              onEdit={item => {
                reset(item);
                setEditData(item);
                setShowShiftBindingForm(true);
              }}
              onRemove={id => setDeleteId(id)}
              deletable={employeeSchedules.length > 1}
            />
          ))}
          <FormProvider {...methods}>
            <ShiftBindingForm
              isOpen={showShiftBindingForm}
              onClose={() => setShowShiftBindingForm(false)}
              staffs={
                staffs?.filter(
                  s =>
                    !selectedStaffs.find(
                      selected =>
                        selected.id === s.id && selected.formId !== id,
                    ),
                ) || []
              }
              units={
                units?.filter(
                  s =>
                    !selectedUnits.find(
                      selected =>
                        selected.id === s.id && selected.formId !== id,
                    ),
                ) || []
              }
              appliedUnitList={[
                {value: 'Company', label: '公司'},
                {value: 'Organization', label: '部門'},
                {value: 'Employee', label: '人員'},
              ].filter(o => o.value !== 'Company' || isCurrentSelectCompany || isCompanyUnselected)}
              shifts={shifts}
              onConfirm={employeeSchedule => {
                if (employeeSchedule.id) {
                  const idx = employeeSchedules.findIndex(
                    i => i.id === employeeSchedule.id,
                  );
                  if (idx !== -1) {
                    onChange([
                      ...employeeSchedules.slice(0, idx),
                      employeeSchedule,
                      ...employeeSchedules.slice(
                        idx + 1,
                        employeeSchedules.length,
                      ),
                    ]);
                  }
                } else {
                  onChange([
                    ...employeeSchedules,
                    {...employeeSchedule, id: uuid.v4()},
                  ]);
                }
                setShowShiftBindingForm(false);
              }}
            />
          </FormProvider>
          <DeleteDialog
            isOpen={deleteId !== null}
            onCancel={() => setDeleteId(null)}
            onConfirm={() => {
              onChange(employeeSchedules.filter(i => i.id !== deleteId));
              setDeleteId(null);
            }}
          />
        </>
      )}
    </FormBlock>
  );
}

export default ShiftBindingBlock;
